import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// GA4 Measurement ID. VITE_GA4_ID env var overrides if set (e.g. for staging
// or to disable hits in local dev by setting it to an empty string).
const GA_ID = import.meta.env.VITE_GA4_ID ?? "G-ZKLECKRF4V";

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

/** Inject the GA4 script tag once */
function loadGA4() {
  if (!GA_ID || document.getElementById("ga4-script")) return;

  const script = document.createElement("script");
  script.id = "ga4-script";
  script.async = true;
  script.defer = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, { send_page_view: false });
}

/** Track custom events */
export function trackEvent(name: string, params?: Record<string, any>) {
  if (!GA_ID || typeof window.gtag !== "function") return;
  window.gtag("event", name, params);
}

/** Track form submissions */
export function trackFormSubmit(formName: string) {
  trackEvent("form_submit", { form_name: formName });
}

/** Track outbound clicks */
export function trackOutboundClick(url: string, label?: string) {
  trackEvent("outbound_click", { link_url: url, link_text: label });
}

/** Track language switch */
export function trackLanguageSwitch(lang: string) {
  trackEvent("language_switch", { language: lang });
}

/** Component that handles GA4 initialization and page view tracking */
export default function Analytics() {
  const location = useLocation();

  useEffect(() => {
    loadGA4();
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (!GA_ID || typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);

  // Scroll depth tracking on blog posts
  useEffect(() => {
    if (!GA_ID || !location.pathname.startsWith("/blog/")) return;

    let tracked50 = false;
    let tracked90 = false;

    const onScroll = () => {
      const scrollPct = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
      if (!tracked50 && scrollPct >= 0.5) {
        tracked50 = true;
        trackEvent("scroll_depth", { percent: 50, page: location.pathname });
      }
      if (!tracked90 && scrollPct >= 0.9) {
        tracked90 = true;
        trackEvent("scroll_depth", { percent: 90, page: location.pathname });
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  return null;
}
